import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, ShieldCheck, CheckCircle2, Globe, Clock, CreditCard, MapPin, Phone, Mail, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { format, isWeekend, isBefore, startOfDay } from "date-fns";
import { COUNTRIES, LANGUAGES, CANADIAN_PROVINCES, CANADIAN_CITIES, GENDERS, TEST_TYPES, TEST_PACKAGES, TERMS_AND_CONDITIONS } from "@/data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo Component - Using uploaded image
const Logo = ({ size = "default" }) => {
  const isLarge = size === "large";
  return (
    <div className="flex items-center gap-2">
      <img 
        src="https://customer-assets.emergentagent.com/job_test-booking-center/artifacts/aqs71l0n_LL.jpg" 
        alt="English Test Solution Logo"
        className={`${isLarge ? 'w-14 h-14' : 'w-10 h-10'} rounded-lg object-cover`}
      />
      <div className={`logo-text ${isLarge ? 'text-2xl' : 'text-xl'}`}>
        English<span className="logo-accent">Test</span>Solution
      </div>
    </div>
  );
};

// Header Component - Removed Admin button
const Header = () => {
  return (
    <header className="glass-header sticky top-0 z-30 py-4 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          <a href="#tests" className="text-slate-600 hover:text-[#1E3A8A] transition-colors font-medium">Tests</a>
          <a href="#register" className="text-slate-600 hover:text-[#1E3A8A] transition-colors font-medium">Register</a>
        </nav>
        <Button 
          className="btn-primary"
          onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })}
          data-testid="header-register-btn"
        >
          Register Now
        </Button>
      </div>
    </header>
  );
};

// Hero Section with highlighted guarantee badge
const HeroSection = () => (
  <section className="hero-gradient py-16 md:py-24 px-6 md:px-12">
    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
      <div className="space-y-6 animate-fade-in-up">
        {/* Highlighted Guarantee Badge */}
        <div className="inline-block">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-lg shadow-lg transform hover:scale-105 transition-transform">
            <div className="flex items-center gap-2 font-bold text-lg">
              <ShieldCheck className="w-6 h-6" />
              <span>Guaranteed Results or Your Money Back</span>
            </div>
          </div>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-tight">
          Pass the English Test. Unlock Canadian Education & Opportunities
        </h1>
        <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
          Preparation for iTEP Academic-Plus and Duolingo English Tests. 
          Book your test at our test centers in Langley & Surrey, BC.
        </p>
        <div className="flex flex-wrap gap-4 pt-4">
          <Button 
            className="btn-primary h-12 px-8 text-lg shadow-md hover:shadow-lg hover:-translate-y-0.5"
            onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="register-now-btn"
          >
            Register Now
          </Button>
        </div>
        <div className="flex flex-wrap gap-6 pt-4">
          <div className="trust-indicator">
            <CheckCircle2 className="w-5 h-5" />
            <span>In Person Test Centre</span>
          </div>
          <div className="trust-indicator">
            <CheckCircle2 className="w-5 h-5" />
            <span>Expert Preparation</span>
          </div>
          <div className="trust-indicator">
            <CheckCircle2 className="w-5 h-5" />
            <span>Refund Guarantee</span>
          </div>
        </div>
      </div>
      <div className="relative animate-fade-in-up animation-delay-200">
        <img 
          src="https://images.unsplash.com/photo-1574660430686-b2a255cfce68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwdGFraW5nJTIwZXhhbSUyMGNvbXB1dGVyfGVufDB8fHx8MTc3MjgzNjQwNHww&ixlib=rb-4.1.0&q=85&w=800"
          alt="Student taking exam"
          className="rounded-2xl shadow-2xl w-full object-cover"
        />
        <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg hidden md:block">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">300+</p>
              <p className="text-sm text-slate-500">Students Passed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Tests Section - All 3 packages in one line
const TestsSection = () => (
  <section id="tests" className="py-16 md:py-24 px-6 md:px-12 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl mb-4">Tests We Prepare You For</h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Choose from internationally recognized English proficiency tests accepted by Canadian institutions
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {/* iTEP Test */}
        <Card className="feature-card">
          <CardHeader className="pb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
              <GraduationCap className="w-6 h-6 text-[#1E3A8A]" />
            </div>
            <CardTitle className="text-xl">iTEP Academic-Plus Test</CardTitle>
            <CardDescription className="text-sm">
              Comprehensive English proficiency assessment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Grammar, Listening, Reading, Writing, Speaking
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                90-minute computer-based test
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Results within 5 business days
              </li>
            </ul>
            <div className="pt-3 border-t">
              <span className="price-currency">C$</span>
              <span className="price-tag">200</span>
              <span className="text-slate-500 text-sm ml-1">+ taxes</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Duolingo Test */}
        <Card className="feature-card">
          <CardHeader className="pb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-3">
              <Globe className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Duolingo English Test</CardTitle>
            <CardDescription className="text-sm">
              Modern, adaptive English test
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Adaptive question format
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                60-minute online test
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Results within 48 hours
              </li>
            </ul>
            <div className="pt-3 border-t">
              <span className="price-currency">C$</span>
              <span className="price-tag">95</span>
              <span className="text-slate-500 text-sm ml-1">+ taxes</span>
            </div>
          </CardContent>
        </Card>

        {/* Prep Package */}
        <Card className="feature-card highlighted relative overflow-hidden">
          <div className="absolute top-3 right-3">
            <span className="badge-accent text-xs">Best Value</span>
          </div>
          <CardHeader className="pb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-[#F97316]" />
            </div>
            <CardTitle className="text-xl">Preparation Package</CardTitle>
            <CardDescription className="text-sm">
              Complete prep with refund guarantee
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Complete study materials
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                One-on-one coaching
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                Money back if you don't pass
              </li>
            </ul>
            <div className="pt-3 border-t">
              <span className="price-currency">C$</span>
              <span className="price-tag">1,000</span>
              <span className="text-slate-500 text-sm ml-1">+ taxes</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// Registration Form Component with Calendar Booking - No Payment
const RegistrationForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  
  // Calendar booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTestCentre, setSelectedTestCentre] = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    test_name: "",
    date_of_birth: "",
    gender: "",
    country_of_residence: "",
    country_of_citizenship: "",
    native_language: "",
    email: "",
    phone: "",
    country: "Canada",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    agency_referred: ""
  });

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/available-slots/${date}`);
      setAvailableSlots(response.data.slots || []);
      setSelectedSlot(null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load available slots');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === "state") {
      setSelectedProvince(value);
      setFormData(prev => ({ ...prev, city: "" }));
    }
  };

  const handlePackageToggle = (pkg) => {
    setSelectedPackages(prev => 
      prev.includes(pkg) ? prev.filter(p => p !== pkg) : [...prev, pkg]
    );
  };

  const calculateTotal = () => {
    let total = 0;
    selectedPackages.forEach(pkg => {
      if (TEST_PACKAGES[pkg]) {
        total += TEST_PACKAGES[pkg].price + TEST_PACKAGES[pkg].tax;
      }
    });
    return total.toFixed(2);
  };

  const validateStep1 = () => {
    const required = ['first_name', 'last_name', 'test_name', 'date_of_birth', 'gender', 'country_of_residence', 'country_of_citizenship', 'native_language'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    const required = ['email', 'phone', 'address', 'city', 'state', 'zip_code', 'agency_referred'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (selectedPackages.length === 0) {
      toast.error('Please select at least one package');
      return false;
    }
    if (!selectedTestCentre) {
      toast.error('Please select a test centre');
      return false;
    }
    if (!selectedDate || !selectedSlot) {
      toast.error('Please select a test date and time slot');
      return false;
    }
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    try {
      const testLabel = TEST_TYPES.find(t => t.value === formData.test_name)?.label || formData.test_name;
      const payload = {
        ...formData,
        phone: `+1${formData.phone}`,
        nationality: formData.country_of_residence,
        test_name: testLabel,
        terms_accepted: termsAccepted,
        selected_packages: selectedPackages,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedSlot,
        test_centre: selectedTestCentre
      };
      
      const response = await axios.post(`${API}/registrations`, payload);
      toast.success('Registration successful!');
      // Navigate directly to success page (no payment)
      navigate(`/success?registration_id=${response.data.id}`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeSlot = (slot) => {
    const [hours, minutes] = slot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  return (
    <section id="register" className="py-16 md:py-24 px-6 md:px-12 bg-[#F8FAFC]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl mb-4">Register for Your Test</h2>
          <p className="text-lg text-slate-600">Complete the form below to begin your journey</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-10">
          <div className="step-indicator">
            <div className={`step-dot ${step >= 1 ? (step > 1 ? 'completed' : 'active') : 'pending'}`}>
              {step > 1 ? <CheckCircle2 className="w-4 h-4" /> : '1'}
            </div>
            <div className={`step-line w-20 ${step > 1 ? 'completed' : ''}`} />
            <div className={`step-dot ${step >= 2 ? (step > 2 ? 'completed' : 'active') : 'pending'}`}>
              {step > 2 ? <CheckCircle2 className="w-4 h-4" /> : '2'}
            </div>
            <div className={`step-line w-20 ${step > 2 ? 'completed' : ''}`} />
            <div className={`step-dot ${step >= 3 ? 'active' : 'pending'}`}>3</div>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in-up">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">First Name *</Label>
                    <Input 
                      className="form-input"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      placeholder="Enter first name"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Last Name *</Label>
                    <Input 
                      className="form-input"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      placeholder="Enter last name"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="form-label">Test Name *</Label>
                  <Select value={formData.test_name} onValueChange={(v) => handleInputChange('test_name', v)}>
                    <SelectTrigger className="form-input" data-testid="test-name-select">
                      <SelectValue placeholder="Select test type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEST_TYPES.map(test => (
                        <SelectItem key={test.value} value={test.value}>{test.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Date of Birth *</Label>
                    <Input 
                      type="date"
                      className="form-input"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      data-testid="dob-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleInputChange('gender', v)}>
                      <SelectTrigger className="form-input" data-testid="gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Country of Residence *</Label>
                    <Select value={formData.country_of_residence} onValueChange={(v) => handleInputChange('country_of_residence', v)}>
                      <SelectTrigger className="form-input" data-testid="residence-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">Country of Citizenship *</Label>
                    <Select value={formData.country_of_citizenship} onValueChange={(v) => handleInputChange('country_of_citizenship', v)}>
                      <SelectTrigger className="form-input" data-testid="citizenship-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="form-label">Native Language *</Label>
                  <Select value={formData.native_language} onValueChange={(v) => handleInputChange('native_language', v)}>
                    <SelectTrigger className="form-input" data-testid="language-select">
                      <SelectValue placeholder="Select native language" />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end pt-4">
                  <Button className="btn-primary" onClick={handleNext} data-testid="step1-next-btn">
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in-up">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Contact Information</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Email Address *</Label>
                    <Input 
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Phone Number *</Label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 bg-slate-100 border border-slate-200 rounded-l-lg text-slate-600 font-medium">
                        +1
                      </div>
                      <Input 
                        className="form-input rounded-l-none flex-1"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleInputChange('phone', value);
                        }}
                        placeholder="6045551234"
                        maxLength={10}
                        data-testid="phone-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Enter 10 digit phone number</p>
                  </div>
                </div>

                <div>
                  <Label className="form-label">Address *</Label>
                  <Input 
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Street address"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Province/State *</Label>
                    <Select value={formData.state} onValueChange={(v) => handleInputChange('state', v)}>
                      <SelectTrigger className="form-input" data-testid="state-select">
                        <SelectValue placeholder="Select province" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANADIAN_PROVINCES.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">City *</Label>
                    <Select 
                      value={formData.city} 
                      onValueChange={(v) => handleInputChange('city', v)}
                      disabled={!selectedProvince}
                    >
                      <SelectTrigger className="form-input" data-testid="city-select">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {(CANADIAN_CITIES[selectedProvince] || []).map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">ZIP/Postal Code *</Label>
                    <Input 
                      className="form-input"
                      value={formData.zip_code}
                      onChange={(e) => handleInputChange('zip_code', e.target.value)}
                      placeholder="A1A 1A1"
                      data-testid="zip-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Agency Referred *</Label>
                    <Input 
                      className="form-input"
                      value={formData.agency_referred}
                      onChange={(e) => handleInputChange('agency_referred', e.target.value)}
                      placeholder="Enter agency name or N/A"
                      data-testid="agency-input"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back-btn">
                    Back
                  </Button>
                  <Button className="btn-primary" onClick={handleNext} data-testid="step2-next-btn">
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Package Selection & Calendar Booking */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-in-up">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Select Package & Book Test Date</h3>
                
                {/* Package Selection */}
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-700">Select Your Package(s)</h4>
                  
                  {/* Test Fee based on selected test */}
                  {formData.test_name && (
                    <div 
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedPackages.includes(formData.test_name) 
                          ? 'border-[#1E3A8A] bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handlePackageToggle(formData.test_name)}
                      data-testid="test-package-option"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={selectedPackages.includes(formData.test_name)}
                            className="pointer-events-none"
                          />
                          <div>
                            <p className="font-medium text-slate-800">
                              {TEST_PACKAGES[formData.test_name]?.name || formData.test_name} - Test Fee
                            </p>
                            <p className="text-sm text-slate-500">Required for taking the test</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#1E3A8A]">
                            C${TEST_PACKAGES[formData.test_name]?.price.toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">
                            + C${TEST_PACKAGES[formData.test_name]?.tax.toFixed(2)} tax
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Prep Package */}
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedPackages.includes('prep_package') 
                        ? 'border-[#F97316] bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handlePackageToggle('prep_package')}
                    data-testid="prep-package-option"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={selectedPackages.includes('prep_package')}
                          className="pointer-events-none"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">Test Preparation Package</p>
                            <span className="badge-accent text-xs">Recommended</span>
                          </div>
                          <p className="text-sm text-slate-500">Complete prep with refund guarantee</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#F97316]">C$1,000.00</p>
                        <p className="text-xs text-slate-500">+ C$130.00 tax</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calendar Booking */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-medium text-slate-700 mb-4">Select Test Centre, Date & Time (90-minute slot)</h4>
                  
                  {/* Test Centre Selection */}
                  <div className="mb-6">
                    <Label className="form-label mb-2">Select Test Centre *</Label>
                    <Select value={selectedTestCentre} onValueChange={setSelectedTestCentre}>
                      <SelectTrigger className="form-input" data-testid="test-centre-select">
                        <SelectValue placeholder="Select test centre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Langley">Langley, BC</SelectItem>
                        <SelectItem value="Surrey">Surrey, BC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Calendar */}
                    <div>
                      <Label className="form-label mb-2">Select Date</Label>
                      <div className="border rounded-lg p-2 bg-white">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={disabledDays}
                          className="rounded-md"
                          data-testid="booking-calendar"
                        />
                      </div>
                    </div>

                    {/* Time Slots */}
                    <div>
                      <Label className="form-label mb-2">Select Time Slot</Label>
                      <div className="border rounded-lg p-4 bg-white min-h-[200px]">
                        {!selectedDate ? (
                          <p className="text-slate-500 text-center py-8">Please select a date first</p>
                        ) : slotsLoading ? (
                          <p className="text-slate-500 text-center py-8">Loading slots...</p>
                        ) : availableSlots.length === 0 ? (
                          <p className="text-slate-500 text-center py-8">No available slots for this date</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot}
                                className={`calendar-slot text-sm ${selectedSlot === slot ? 'selected' : ''}`}
                                onClick={() => setSelectedSlot(slot)}
                                data-testid={`slot-${slot}`}
                              >
                                {formatTimeSlot(slot)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedDate && selectedSlot && selectedTestCentre && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
                          <p className="font-medium text-slate-800">Selected Appointment:</p>
                          <p className="text-slate-600">
                            {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {formatTimeSlot(selectedSlot)}
                          </p>
                          <p className="text-slate-600">Test Centre: {selectedTestCentre}, BC</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-700">Total Amount:</span>
                    <span className="text-2xl font-bold text-[#1E3A8A]">C${calculateTotal()}</span>
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <Checkbox 
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    data-testid="terms-checkbox"
                  />
                  <div>
                    <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                      I have read and agree to the{" "}
                      <button 
                        type="button"
                        className="text-[#1E3A8A] underline font-medium"
                        onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                        data-testid="terms-link"
                      >
                        Terms and Conditions
                      </button>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="step3-back-btn">
                    Back
                  </Button>
                  <Button 
                    className="btn-primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="submit-registration-btn"
                  >
                    {loading ? 'Submitting...' : 'Complete Registration'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Terms Dialog */}
        <Dialog open={showTerms} onOpenChange={setShowTerms}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Terms and Conditions</DialogTitle>
              <DialogDescription>Please read carefully before accepting</DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[50vh] pr-4">
              <div 
                className="prose prose-sm max-w-none text-slate-700"
                dangerouslySetInnerHTML={{ __html: TERMS_AND_CONDITIONS }}
              />
            </ScrollArea>
            <DialogFooter>
              <Button 
                onClick={() => { setTermsAccepted(true); setShowTerms(false); }}
                data-testid="accept-terms-btn"
              >
                I Accept
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// Footer - Using uploaded logo
const Footer = () => (
  <footer className="bg-[#0F172A] text-white py-12 px-6 md:px-12">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-3 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_test-booking-center/artifacts/aqs71l0n_LL.jpg" 
              alt="Logo"
              className="w-10 h-10 rounded-lg"
            />
            <span className="font-bold text-xl">EnglishTestSolution</span>
          </div>
          <p className="text-slate-400 text-sm">
            Your trusted partner for English proficiency test preparation and booking in Canada.
          </p>
        </div>
        <div>
          <h4 className="font-semibold mb-4 text-white">Test Centres</h4>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Langley, BC
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Surrey, BC
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4 text-white">Contact Us</h4>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> info@englishtestsolution.com
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
        <p>&copy; 2026 EnglishTestSolution.com. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Main HomePage Component
export default function HomePage() {
  return (
    <div className="min-h-screen">
      <Header />
      <HeroSection />
      <TestsSection />
      <RegistrationForm />
      <Footer />
    </div>
  );
}
